/**
 * FEN → Maia 3 input tensors.
 *
 * Maia 3 takes three inputs per position:
 *   1. `tokens` — Float32Array shaped (64, 12), flat. One-hot encoding of
 *      piece-on-square. Channels 0-5 are white P,N,B,R,Q,K; 6-11 are
 *      black p,n,b,r,q,k. Always from WHITE's perspective: if it's
 *      black to move, the board (and colors) are mirrored before encoding.
 *   2. `eloSelf` — float, rating of side to move.
 *   3. `eloOppo` — float, rating of the other side.
 *
 * We also compute a legal-move mask (length 4352, 1.0 at indices that are
 * legal UCI moves FROM WHITE'S PERSPECTIVE after any mirroring) so the
 * caller can filter the policy logits before softmax.
 *
 * This module is pure — no I/O, no React, no workers. Testable in isolation.
 */

import { Chess } from 'chess.js';
import { MOVE_INDEX, mirrorMove } from './moves';

// White pieces in channels 0-5, black in 6-11. Index matches FEN char.
const PIECE_CHARS = ['P', 'N', 'B', 'R', 'Q', 'K', 'p', 'n', 'b', 'r', 'q', 'k'];

/** Convert a board's piece-placement string into a (64, 12) flat tensor. */
function piecesToTokens(piecePlacement: string): Float32Array {
  const tokens = new Float32Array(64 * 12);
  const rows = piecePlacement.split('/');
  // FEN ranks go 8→1 top-to-bottom. Square index is rank*8 + file with rank 0 = rank 1.
  for (let rowIdx = 0; rowIdx < 8; rowIdx++) {
    const rank = 7 - rowIdx;
    let file = 0;
    for (const ch of rows[rowIdx]) {
      const digit = parseInt(ch, 10);
      if (!Number.isNaN(digit)) {
        file += digit;
        continue;
      }
      const pieceIdx = PIECE_CHARS.indexOf(ch);
      if (pieceIdx >= 0) {
        const square = rank * 8 + file;
        tokens[square * 12 + pieceIdx] = 1;
      }
      file += 1;
    }
  }
  return tokens;
}

/** Swap case for every letter — "PPpp" → "ppPP". */
function swapColorsInRank(rank: string): string {
  let out = '';
  for (const c of rank) {
    if (c >= 'A' && c <= 'Z') out += c.toLowerCase();
    else if (c >= 'a' && c <= 'z') out += c.toUpperCase();
    else out += c;
  }
  return out;
}

function swapCastlingRights(castling: string): string {
  if (castling === '-') return '-';
  const has = new Set(castling);
  let out = '';
  // Canonical order KQkq, after color swap.
  if (has.has('k')) out += 'K';
  if (has.has('q')) out += 'Q';
  if (has.has('K')) out += 'k';
  if (has.has('Q')) out += 'q';
  return out === '' ? '-' : out;
}

/**
 * Mirror a FEN vertically and swap piece colors. Turns "black to move" into
 * the equivalent "white to move" board from White's perspective — Maia 3's
 * input is always White-to-move.
 */
export function mirrorFen(fen: string): string {
  const parts = fen.split(' ');
  if (parts.length < 4) throw new Error(`invalid FEN: ${fen}`);
  const [pos, active, castling, ep, halfmove = '0', fullmove = '1'] = parts;

  const mirroredPos = pos
    .split('/')
    .reverse()
    .map(swapColorsInRank)
    .join('/');
  const mirroredActive = active === 'w' ? 'b' : 'w';
  const mirroredCastling = swapCastlingRights(castling);
  const mirroredEp =
    ep !== '-' ? ep[0] + String(9 - parseInt(ep[1], 10)) : '-';

  return `${mirroredPos} ${mirroredActive} ${mirroredCastling} ${mirroredEp} ${halfmove} ${fullmove}`;
}

export interface MaiaInputs {
  /** (64, 12) flat one-hot tensor, White perspective. */
  tokens: Float32Array;
  /** Length-4352 mask, 1.0 at legal move indices in the White-oriented vocab. */
  legalMoves: Float32Array;
  /** True iff the input was mirrored (black was to move). */
  wasMirrored: boolean;
}

/**
 * Convert a FEN into Maia 3's fixed input shape.
 *
 * For black-to-move positions, the FEN is mirrored so the model always sees
 * White-to-move. The caller must re-mirror any move picked from the policy
 * head — `wasMirrored` carries that signal.
 */
export function preprocessFen(fen: string): MaiaInputs {
  const parts = fen.split(' ');
  const active = parts[1];
  if (active !== 'w' && active !== 'b') {
    throw new Error(`invalid FEN (active color): ${fen}`);
  }

  const wasMirrored = active === 'b';
  const effectiveFen = wasMirrored ? mirrorFen(fen) : fen;

  const tokens = piecesToTokens(effectiveFen.split(' ')[0]);

  const legalMoves = new Float32Array(4352);
  const board = new Chess(effectiveFen);
  for (const move of board.moves({ verbose: true })) {
    const uci = move.from + move.to + (move.promotion ?? '');
    const idx = MOVE_INDEX.get(uci);
    if (idx !== undefined) legalMoves[idx] = 1;
  }

  return { tokens, legalMoves, wasMirrored };
}

/**
 * Given the move the model selected (an index into the 4352 vocab), turn it
 * back into a UCI string for the ORIGINAL position. If we mirrored the board
 * before inference, we have to mirror the move back.
 */
export function decodeMove(index: number, wasMirrored: boolean): string {
  const uci = MOVE_INDEX_REVERSE[index];
  if (!uci) throw new Error(`invalid move index: ${index}`);
  return wasMirrored ? mirrorMove(uci) : uci;
}

// Lazy reverse lookup — built once.
const MOVE_INDEX_REVERSE: readonly string[] = (() => {
  const out: string[] = new Array(4352);
  MOVE_INDEX.forEach((idx, uci) => {
    out[idx] = uci;
  });
  return out;
})();
